import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { NumberInputRow } from '@/components/form/NumberInputRow';
import { ToggleSection } from '@/components/form/ToggleSection';
import { ConfigSection } from '@/components/config_forms/shared/ConfigSection';
import { ExpenseCategoryRow } from './ExpenseCategoryRow';
import { createCustomExpenseCategory } from '@/constants/expenses';
import type { ExpenseConfig } from '@/types/ExpenseConfig';
import type { ExpenseCategory } from '@/types/ExpenseCategory';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Receipt, Plus, Briefcase, Globe } from 'lucide-react';

interface ExpenseConfigFormProps {
  title: string;
  description: string;
  // New structured categories
  expenseCategories: ExpenseCategory[];
  onCategoriesChange: (categories: ExpenseCategory[]) => void;
  // Existing global expense settings (kept)
  config: ExpenseConfig;
  onConfigChange: (updates: Partial<ExpenseConfig>) => void;
  mileageRateProps: Record<string, unknown>;
  mileageRateError?: React.ReactNode;
  receiptRequirementProps: Record<string, unknown>;
  receiptRequirementError?: React.ReactNode;
  markupPercentageProps: Record<string, unknown>;
  markupPercentageError?: React.ReactNode;
  markupFixedAmountProps: Record<string, unknown>;
  markupFixedAmountError?: React.ReactNode;
  isCustomized?: boolean;
  onRevert?: () => void;
  context: 'user' | 'client';
}

export const ExpenseConfigForm: React.FC<ExpenseConfigFormProps> = ({
  title,
  description,
  expenseCategories,
  onCategoriesChange,
  config,
  onConfigChange,
  mileageRateProps,
  mileageRateError,
  receiptRequirementProps,
  receiptRequirementError,
  markupPercentageProps,
  markupPercentageError,
  markupFixedAmountProps,
  markupFixedAmountError,
  isCustomized,
  onRevert,
  context,
}) => {
  const [allBookingsOpen, setAllBookingsOpen] = useState(true);
  const [travelOnlyOpen, setTravelOnlyOpen] = useState(true);

  const allBookingsCategories = expenseCategories.filter((c) => c.scope === 'all');
  const travelOnlyCategories = expenseCategories.filter((c) => c.scope === 'travel_only');

  const handleCategoryChange = (updated: ExpenseCategory) => {
    onCategoriesChange(
      expenseCategories.map((c) => (c.id === updated.id ? updated : c))
    );
  };

  const handleDeleteCategory = (id: string) => {
    onCategoriesChange(expenseCategories.filter((c) => c.id !== id));
  };

  const handleAddCustomCategory = (scope: ExpenseCategory['scope']) => {
    onCategoriesChange([
      ...expenseCategories,
      createCustomExpenseCategory({ scope, name: 'New Category' }),
    ]);
  };

  const content = (
    <div className="space-y-6">
      <div className="space-y-1 text-center">
        <div className="flex items-center justify-center gap-3">
          <Receipt className="h-5 w-5 text-primary" />
          <h3 className="text-xl font-semibold text-foreground">{title}</h3>
          {isCustomized && <Badge variant="default">{context === 'client' ? 'Client Override' : 'Profile Default'}</Badge>}
        </div>
        <p className="text-sm text-muted-foreground px-2 max-w-lg mx-auto leading-relaxed">{description}</p>
      </div>

      {/* Expense Categories by Scope */}
      <div className="space-y-4">
        <ConfigSection
          title="All Bookings"
          description="Expense categories that apply to every booking"
          icon={Globe}
          isOpen={allBookingsOpen}
          onOpenChange={setAllBookingsOpen}
        >
          <div className="space-y-3">
            {allBookingsCategories.map((cat) => (
              <ExpenseCategoryRow
                key={cat.id}
                category={cat}
                onChange={handleCategoryChange}
                onDelete={() => handleDeleteCategory(cat.id)}
                showScope={false}
              />
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => handleAddCustomCategory('all')}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Custom Category
            </Button>
          </div>
        </ConfigSection>

        <ConfigSection
          title="Travel Only"
          description="Expense categories that only apply to travel bookings"
          icon={Briefcase}
          isOpen={travelOnlyOpen}
          onOpenChange={setTravelOnlyOpen}
        >
          <div className="space-y-3">
            {travelOnlyCategories.map((cat) => (
              <ExpenseCategoryRow
                key={cat.id}
                category={cat}
                onChange={handleCategoryChange}
                onDelete={() => handleDeleteCategory(cat.id)}
                showScope={false}
              />
            ))}
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => handleAddCustomCategory('travel_only')}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add Custom Travel Category
            </Button>
          </div>
        </ConfigSection>
      </div>

      {/* Global Expense Settings */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="text-sm font-medium text-muted-foreground text-center uppercase tracking-wider">Global Expense Settings</h4>

        <NumberInputRow
          id="mileage-rate"
          label="Mileage Rate"
          description="Your standard reimbursement rate per mile/km."
          unit="$"
          unitPosition="left"
          inputProps={mileageRateProps}
          errorElement={mileageRateError}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4 md:items-center">
          <Label>Mileage Unit</Label>
          <RadioGroup
            value={config.expenseMileageUnit || 'miles'}
            onValueChange={(v) => onConfigChange({ expenseMileageUnit: v as 'miles' | 'km' })}
            className="flex gap-4 pt-1"
          >
            <div className="flex items-center space-x-2"><RadioGroupItem value="miles" id="miles" /><Label htmlFor="miles">Miles</Label></div>
            <div className="flex items-center space-x-2"><RadioGroupItem value="km" id="km" /><Label htmlFor="km">Kilometers</Label></div>
          </RadioGroup>
        </div>

        <NumberInputRow
          id="receipt-requirement"
          label="Receipts Required Above"
          description="Expenses above this amount will require a receipt."
          unit="$"
          unitPosition="left"
          inputProps={receiptRequirementProps}
          errorElement={receiptRequirementError}
        />

        <ToggleSection
          id="use-markup"
          label="Apply Default Markup"
          description="Add a default administrative markup to billable expenses."
          checked={config.defaultExpenseMarkupType !== 'none' && config.defaultExpenseMarkupType !== null}
          onCheckedChange={(checked) => onConfigChange({ defaultExpenseMarkupType: checked ? 'percentage' : 'none' })}
          variant="indented"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 md:gap-4 md:items-center">
            <Label>Markup Type</Label>
            <RadioGroup
              value={config.defaultExpenseMarkupType || 'percentage'}
              onValueChange={(v) => onConfigChange({ defaultExpenseMarkupType: v as ExpenseConfig['defaultExpenseMarkupType'] })}
              className="flex gap-4 pt-1"
            >
              <div className="flex items-center space-x-2"><RadioGroupItem value="percentage" id="markup-pct" /><Label htmlFor="markup-pct">Percentage</Label></div>
              <div className="flex items-center space-x-2"><RadioGroupItem value="fixed_amount" id="markup-fixed" /><Label htmlFor="markup-fixed">Fixed Amount</Label></div>
            </RadioGroup>
          </div>
          <AnimatePresence mode="wait">
            {config.defaultExpenseMarkupType === 'percentage' && (
              <motion.div key="pct" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <NumberInputRow
                  id="markup-percentage"
                  label="Markup Percentage"
                  unit="%"
                  inputProps={markupPercentageProps}
                  errorElement={markupPercentageError}
                />
              </motion.div>
            )}
            {config.defaultExpenseMarkupType === 'fixed_amount' && (
              <motion.div key="fixed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <NumberInputRow
                  id="markup-fixed-amount"
                  label="Markup Amount"
                  unit="$"
                  unitPosition="left"
                  inputProps={markupFixedAmountProps}
                  errorElement={markupFixedAmountError}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </ToggleSection>
      </div>

      {isCustomized && onRevert && (
        <div className="pt-4 border-t flex justify-center">
          <Button variant="link" className="p-0 h-auto text-muted-foreground" onClick={onRevert}>
            Revert to Profile Defaults
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <Card className={cn("animate-fade-in shadow-sm rounded-xl p-4 md:p-6", context === 'user' ? 'w-full' : 'max-w-3xl mx-auto')}>
      {content}
    </Card>
  );
};
